/**
 * ScrollAnimation
 * スクロール時のアニメーション制御
 *
 * @usage
 * HTML: <div class="js-scroll-anim" data-anim="fadeInUp">コンテンツ</div>
 * JS: new ScrollAnimation();
 */
export default class ScrollAnimation {
  constructor(options = {}) {
    this.options = {
      selector: '.js-scroll-anim',
      activeClass: 'is-animated',
      offset: 0.2, // 要素が画面に20%見えたらアニメーション開始
      once: true,   // 一度だけアニメーション実行
      ...options
    };

    this.elements = document.querySelectorAll(this.options.selector);
    this.observer = null;

    if (this.elements.length > 0) {
      this.init();
    }
  }

  init() {
    // Intersection Observer APIを使用
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        threshold: this.options.offset,
        rootMargin: '0px 0px -10% 0px'
      }
    );

    // 各要素を監視対象に追加
    this.elements.forEach(element => {
      this.observer.observe(element);
    });
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.animateElement(entry.target);
        
        // 一度だけアニメーション実行する場合は監視を停止
        if (this.options.once) {
          this.observer.unobserve(entry.target);
        }
      } else if (!this.options.once) {
        // once: false の場合、画面外に出たらアニメーションクラスを削除
        entry.target.classList.remove(this.options.activeClass);
      }
    });
  }

  animateElement(element) {
    // アニメーション実行
    element.classList.add(this.options.activeClass);

    // data-anim-delay属性がある場合は遅延実行
    const delay = element.dataset.animDelay;
    if (delay) {
      element.style.animationDelay = `${delay}ms`;
    }

    // カスタムイベントの発火
    element.dispatchEvent(new CustomEvent('scroll-anim:animated', {
      detail: { element }
    }));
  }

  // 手動でアニメーションをトリガー
  trigger(selector) {
    const elements = typeof selector === 'string' 
      ? document.querySelectorAll(selector)
      : [selector];

    elements.forEach(element => {
      if (element && this.elements.includes && this.elements.includes(element)) {
        this.animateElement(element);
      }
    });
  }

  // 特定の要素の監視を再開
  restart(selector) {
    const elements = typeof selector === 'string' 
      ? document.querySelectorAll(selector)
      : [selector];

    elements.forEach(element => {
      if (element) {
        element.classList.remove(this.options.activeClass);
        this.observer.observe(element);
      }
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    // アニメーションクラスを削除
    this.elements.forEach(element => {
      element.classList.remove(this.options.activeClass);
    });
  }
}